import React, { useEffect, useState } from 'react';

import Select from 'react-select';

import UsersTrashedRelations from './UsersTrashedRelations';

import { validateFormField } from 'Helpers/formValidation';
import { multiSelectConfigs, prepareRelationsData, onChangeUserAssociations, listOfResources } from 'Helpers/multi-select';

import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

function UsersTableRow(props) {
    const [user, setUser] = useState(props.user);
    const [superAdminRole, setSuperAdminRole] = useState(props.superAdminRole);
    const [edit, setEdit] = useState(false);
    const [roles, setRoles] = useState(props.roles);
    const [departments, setDepartments] = useState(props.departments);
    const [revDepartments, setRevDepartments] = useState(props.revDepartments);
    const [venues, setVenues] = useState(props.venues);
    const [costcenters, setCostcenters] = useState(props.costcenters);
    const [revCostcenters, setRevCostcenters] = useState(props.revCostcenters);
    const [modules, setModules] = useState(props.modules || []);

    const defaultErrors = {
        firstName: null,
        lastName: null,
    };
    const [formErrors, setFormErrors] = useState(defaultErrors);

    const getExistingAssociation = (user, associationName, fixedId = null) => {
        let array = [];
        if (associationName === 'modules') {
            user.event_modules.forEach((eventModule) => {
                array.push(eventModule.module);
            });
        } else {
            array = associationName === 'roles' ? user.event_users[0][associationName] : user[associationName];
        }

        return array.map((item) => {
            const result = {
                value: item.id,
                label: item.name,
            };

            if (fixedId) {
                result.isFixed = parseInt(item.id) === parseInt(fixedId);
            }

            return result;
        });
    };

    const [firstName, setFirstName] = useState(user.first_name);
    const [lastName, setLastName] = useState(user.last_name);
    const [enabled, setEnabled] = useState(!!user.pivot.is_active);
    const [formDisabled, setFormDisabled] = useState(false);

    const [rolesData, setRolesData] = useState(getExistingAssociation(user, 'roles', superAdminRole.id));
    const [departmentsData, setDepartmentsData] = useState(getExistingAssociation(user, 'departments'));
    const [costcentersData, setCostcentersData] = useState(getExistingAssociation(user, 'costcenters'));
    const [revDepartmentsData, setRevDepartmentsData] = useState(getExistingAssociation(user, 'revdepartments'));
    const [revCostcentersData, setRevCostcentersData] = useState(getExistingAssociation(user, 'revcostcenters'));
    const [venuesData, setVenuesData] = useState(getExistingAssociation(user, 'venues'));
    const [modulesData, setModulesData] = useState(getExistingAssociation(user, 'modules'));

    useEffect(() => {
        setFormDisabled(formHasErrors() || !formFilled());
    }, [formErrors]);

    const formHasErrors = () => {
        let hasErrors = false;

        Object.values(formErrors).forEach((val) => {
            if (val !== null) {
                hasErrors = true;
            }
        });

        return hasErrors;
    };

    const formFilled = () => {
        return firstName.length > 1 && lastName.length;
    };

    function hasSuperAdminRole(roles) {
        let result = false;
        roles.forEach((role) => {
            if (parseInt(role.id) === parseInt(superAdminRole.id)) {
                result = true;
            }
        });

        return result;
    }

    const canEnable = () => {
        return props.canEnable && !hasSuperAdminRole(user.roles);
    };

    const listOfRoles = listOfResources(roles, superAdminRole.id);
    const listOfDepartments = listOfResources(departments);
    const listOfRevDepartments = listOfResources(revDepartments);
    const listOfVenues = listOfResources(venues);
    const listOfCostcenters = listOfResources(costcenters);
    const listOfRevCostcenters = listOfResources(revCostcenters);
    const listOfModules = listOfResources(modules);

    const styles = { ...multiSelectConfigs };

    const validateField = (fieldName, value) => {
        const errors = { ...formErrors };
        const validationErrors = validateFormField(fieldName, value);
        Object.keys(validationErrors).forEach((key) => {
            if (key in errors) {
                errors[key] = validationErrors[key];
            }
        });

        setFormErrors(errors);
    };

    const validateForm = () => {
        validateField('firstName', firstName);
        validateField('lastName', lastName);
    };

    const clickRow = () => {
        if (!props.canEdit) {
            return;
        }
        setEdit(true);
    };

    const onChangeRoles = (e, option) => {
        onChangeUserAssociations(e, option, setRolesData);
    };

    const onChangeDepartments = (e, option) => {
        onChangeUserAssociations(e, option, setDepartmentsData);
    };

    const onChangeRevDepartments = (e, option) => {
        onChangeUserAssociations(e, option, setRevDepartmentsData);
    };

    const onChangeVenues = (e, option) => {
        onChangeUserAssociations(e, option, setVenuesData);
    };

    const onChangeModules = (e, option) => {
        onChangeUserAssociations(e, option, setModulesData);
    };

    const onChangeCostcenters = (e, option) => {
        onChangeUserAssociations(e, option, setCostcentersData);
    };

    const onChangeRevCostcenters = (e, option) => {
        onChangeUserAssociations(e, option, setRevCostcentersData);
    };

    const firstNameChange = (e) => {
        setFirstName(e.target.value);
    };

    const lastNameChange = (e) => {
        setLastName(e.target.value);
    };

    function cancelEdit(user = null) {
        setEdit(false);

        const data = user || props.user;
        setUser(data);
        setFirstName(data.first_name);
        setLastName(data.last_name);
        setFormErrors(defaultErrors);

        if (data.event_users[0] !== undefined) {
            setRolesData(
                data.event_users[0].roles.map((item) => ({
                    value: item.id,
                    label: item.name,
                    isFixed: parseInt(item.id) === parseInt(superAdminRole.id),
                })),
            );
        }
    }

    function save() {
        validateForm();
        if (formHasErrors() || !props.canEdit) {
            return;
        }

        const { rolesIds, departmentsIds, venuesIds, costcentersIds, revdepartmentsIds, revcostcentersIds, modulesIds } = prepareRelationsData(
            rolesData,
            departmentsData,
            venuesData,
            costcentersData,
            revDepartmentsData,
            revCostcentersData,
            modulesData,
        );

        setFormDisabled(true);

        axios
            .put(`${props.updateUserUrl}`, {
                firstName,
                lastName,
                enabled,
                rolesIds,
                departmentsIds,
                venuesIds,
                costcentersIds,
                revdepartmentsIds,
                revcostcentersIds,
                modulesIds,
            })
            .then((response) => {
                setFormDisabled(false);
                const { user } = response.data;
                cancelEdit(user);
            })
            .catch((e) => {
                setFormDisabled(false);
                console.log(e);
                cancelEdit(null);
            });
    }

    return (
        <>
            {!edit ? (
                <tr key={user.id}>
                    <td>{user.first_name + ' ' + user.last_name}</td>
                    <td className="users-email-cell tw-break-words">{user.email}</td>
                    <td>{user.event_users[0].roles.length === 0 ? $t('No assigned roles') : user.event_users[0].roles.map((elem) => elem.name).join(' | ')}</td>
                    <td>
                        <div>{user.departments.length === 0 ? $t('No assigned departments') : user.departments.map((elem) => elem.name).join(' | ')}</div>
                        <div className="tw-space-y-1 tw-mt-2">
                            {user.trashed_departments !== undefined && user.trashed_departments.length > 0 && (
                                <UsersTrashedRelations trashed={user.trashed_departments} name="venues" />
                            )}
                        </div>
                    </td>
                    <td>
                        <div>{user.venues.length === 0 ? $t('No assigned venues') : user.venues.map((elem) => elem.name).join(' | ')}</div>
                        <div className="tw-space-y-1 tw-mt-2">
                            {user.trashed_venues !== undefined && user.trashed_venues.length > 0 && (
                                <UsersTrashedRelations trashed={user.trashed_venues} name="venues" />
                            )}
                        </div>
                    </td>
                    <td>
                        <div>{user.costcenters.length === 0 ? $t('No assigned cost centers') : user.costcenters.map((elem) => elem.name).join(' | ')}</div>
                        <div className="tw-space-y-1 tw-mt-2">
                            {user.trashed_costcenters !== undefined && user.trashed_costcenters.length > 0 && (
                                <UsersTrashedRelations trashed={user.trashed_costcenters} name="costcenters" />
                            )}
                        </div>
                    </td>
                    <td>
                        <div>
                            {user.revdepartments.length === 0 ? $t('No assigned rev departments') : user.revdepartments.map((elem) => elem.name).join(' | ')}
                        </div>
                        <div className="tw-space-y-1 tw-mt-2">
                            {user.trashed_revdepartments !== undefined && user.trashed_revdepartments.length > 0 && (
                                <UsersTrashedRelations trashed={user.trashed_revdepartments} name="revdepartments" />
                            )}
                        </div>
                    </td>
                    <td>
                        <div>
                            {user.revcostcenters.length === 0 ? $t('No assigned rev cost centers') : user.revcostcenters.map((elem) => elem.name).join(' | ')}
                        </div>
                        {user.trashed_revcostcenters !== undefined && user.trashed_revcostcenters.length > 0 && (
                            <UsersTrashedRelations trashed={user.trashed_revcostcenters} name="revcostcenters" />
                        )}
                    </td>
                    <td className="users-modules-cell">
                        <div>{user.event_modules.length === 0 ? $t('No enabled modules') : user.event_modules.map((elem) => elem.module.name).join(' | ')}</div>
                    </td>
                    <td>{parseInt(user.pivot.is_active) > 0 ? $t('Yes') : $t('No')}</td>
                    <td>
                        <div className="users-actions tw-flex tw-items-center tw-gap-x-3">
                            {props.canEdit && (
                                <TextButton variant="primary" onClick={clickRow}>
                                    <EditIcon className="tw-w-3 tw-h-3" /> {$t('Edit')}
                                </TextButton>
                            )}
                            {props.canDelete && (
                                <TextButton variant="danger" onClick={() => props.confirmDeleteHandler(props.user)}>
                                    <TrashIcon className="tw-w-3 tw-h-3" />
                                    {$t('Delete')}
                                </TextButton>
                            )}
                        </div>
                    </td>
                </tr>
            ) : (
                <tr key={user.id} className="tw-bg-grey">
                    <td colSpan={11}>
                        <form>
                            <div className="tw-grid tw-grid-cols-6 tw-gap-4">
                                <div className="tw-col-span-1 text-blue">{$t('First Name')}</div>
                                <div className="tw-col-span-2">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            className={`form-control bg-white ${formErrors.firstName ? 'is-invalid border border-danger' : ''}`}
                                            id="inputFirstName"
                                            placeholder={$t('First Name')}
                                            value={firstName}
                                            onChange={firstNameChange}
                                            onBlur={() => {
                                                validateField('firstName', firstName);
                                            }}
                                        />
                                        <div className={'col-sm-12 text-xs'}>
                                            {formErrors.firstName && <span className="text-danger">{formErrors.firstName}</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="tw-col-span-1 text-blue">{$t('Venues')}</div>
                                <div className="tw-col-span-2">
                                    <div className="form-group">
                                        <Select
                                            value={venuesData}
                                            isMulti
                                            name="venues"
                                            options={listOfVenues || []}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            onChange={onChangeVenues}
                                            styles={styles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="tw-grid tw-grid-cols-6 tw-gap-4">
                                <div className="tw-col-span-1 text-blue">{$t('Last Name')}</div>
                                <div className="tw-col-span-2">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            className={`form-control bg-white ${formErrors.lastName ? 'is-invalid border border-danger' : ''}`}
                                            id="inputLastName"
                                            placeholder={$t('Last Name')}
                                            value={lastName}
                                            onChange={lastNameChange}
                                            onBlur={() => {
                                                validateField('lastName', lastName);
                                            }}
                                        />
                                        {formErrors.lastName && <span className="text-danger">{formErrors.lastName}</span>}
                                    </div>
                                </div>
                                <div className="tw-col-span-1 text-blue">{$t('Cost Centers')}</div>
                                <div className="tw-col-span-2">
                                    <div className="form-group">
                                        <Select
                                            value={costcentersData}
                                            isMulti
                                            name="costcenters"
                                            options={listOfCostcenters || []}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            onChange={onChangeCostcenters}
                                            styles={styles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="tw-grid tw-grid-cols-6 tw-gap-4">
                                <div className="tw-col-span-1 text-blue">{$t('Email')}</div>
                                <div className="tw-col-span-2">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            className={`form-control bg-white ${formErrors.lastName ? 'is-invalid border border-danger' : ''}`}
                                            id="inputEmail"
                                            placeholder="Email"
                                            value={user.email}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                                <div className="tw-col-span-1 text-blue">{$t('Rev Departments')}</div>
                                <div className="tw-col-span-2">
                                    <div className="form-group">
                                        <Select
                                            value={revDepartmentsData}
                                            isMulti
                                            name="revdepartments"
                                            options={listOfRevDepartments || []}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            onChange={onChangeRevDepartments}
                                            styles={styles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="tw-grid tw-grid-cols-6 tw-gap-4">
                                <div className="tw-col-span-1 text-blue">{$t('Roles')}</div>
                                <div className="tw-col-span-2">
                                    <div className="form-group">
                                        <Select
                                            value={rolesData}
                                            isMulti
                                            name="roles"
                                            options={listOfRoles || []}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            onChange={onChangeRoles}
                                            styles={styles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                </div>
                                <div className="tw-col-span-1 text-blue">{$t('Rev Cost Centers')}</div>
                                <div className="tw-col-span-2">
                                    <div>
                                        <Select
                                            value={revCostcentersData}
                                            isMulti
                                            name="revcostcenters"
                                            options={listOfRevCostcenters || []}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            onChange={onChangeRevCostcenters}
                                            styles={styles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="tw-grid tw-grid-cols-6 tw-gap-4">
                                <div className="tw-col-span-1 text-blue">{$t('Departments')}</div>
                                <div className="tw-col-span-2">
                                    <div className="form-group">
                                        <Select
                                            value={departmentsData}
                                            isMulti
                                            name="departments"
                                            options={listOfDepartments || []}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            onChange={onChangeDepartments}
                                            styles={styles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                </div>
                                <div className="tw-col-span-1 text-blue">{$t('Modules')}</div>
                                <div className="tw-col-span-2">
                                    <div className="form-group">
                                        <Select
                                            value={modulesData}
                                            isMulti
                                            name="modules"
                                            options={listOfModules || []}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            onChange={onChangeModules}
                                            styles={styles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="tw-grid tw-grid-cols-6 tw-gap-4">
                                <div className="tw-col-span-1 text-blue">{$t('Enabled')}</div>
                                <div className="tw-col-span-2 text-left">
                                    <span className={'pr-2'}>{enabled ? $t('Yes') : $t('No')}</span>
                                    <input type="checkbox" defaultChecked={enabled} onChange={() => setEnabled(!enabled)} disabled={!canEnable()} />
                                </div>
                            </div>

                            <div className={'col tw-flex tw-justify-end tw-gap-x-1'}>
                                <Button variant="secondary" onClick={() => cancelEdit(null)}>
                                    {$t('Cancel')}
                                </Button>
                                {props.canEdit && (
                                    <Button variant={formDisabled ? 'secondary' : 'primary'} onClick={save} disabled={formDisabled}>
                                        {$t('Save')}
                                    </Button>
                                )}
                            </div>
                        </form>
                    </td>
                </tr>
            )}
        </>
    );
}

export default UsersTableRow;
